import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCreditCard, FaShieldAlt, FaCheck } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal bloqueante que aparece cuando una clienta VIP fue creada con
 * `payment_status: 'pending'`. Le permite ver mensaje de bienvenida y
 * pagar el valor asignado para desbloquear el resto de la app.
 */
export default function PendingPaymentModal({ user, profile, onPaid }) {
  const [loading, setLoading] = useState(false);
  const cop = profile?.pending_payment_amount_cop;
  const usd = profile?.pending_payment_amount_usd;

  // Detectar moneda preferida: si tiene COP usar COP; si solo USD usar USD
  const useUSD = !cop && !!usd;
  const displayAmount = useUSD ? usd : cop;
  const currency = useUSD ? 'USD' : 'COP';
  const formatted = useUSD
    ? `$${Number(usd).toFixed(2)} USD`
    : `$${Number(cop).toLocaleString('es-CO')} COP`;

  const handlePay = async () => {
    if (!displayAmount || displayAmount <= 0) {
      toast.error('No hay un monto asignado para pagar. Contacta a tu entrenadora.');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const payload = {
        payment_method: 'mercadopago',
        product_type: 'subscription',
        plan_type: profile?.subscription_plan || 'premium',
        plan_name: `Membresía ${profile?.subscription_plan || 'premium'}`,
        subscription_period: profile?.subscription_period || 'monthly',
        title: `Membresía DeisyCFit${user?.name ? ' - ' + user.name : ''}`,
        currency_id: currency,
        amount: Number(displayAmount),
        vip_pending_payment: true,
        return_url: `${window.location.origin}/dashboard`
      };
      const res = await axios.post(
        `${API}/api/payments/mercadopago/create`,
        payload,
        { headers: { Authorization: token ? `Bearer ${token}` : '' } }
      );
      const checkoutUrl = res.data?.checkout_url || res.data?.init_point || res.data?.sandbox_init_point;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        toast.error('No pudimos generar el link de pago. Contacta a soporte.');
      }
    } catch (error) {
      console.error('Pending payment error:', error?.response?.data || error);
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No pudimos iniciar el pago. Intenta de nuevo en unos minutos.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      data-testid="pending-payment-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-pink-500/30"
      >
        {/* Header */}
        <div
          className="p-6 text-center text-white"
          style={{ background: 'linear-gradient(135deg, var(--brand-primary), #C084FC)' }}
        >
          <div className="text-4xl mb-2">🎉</div>
          <h2 className="text-xl font-bold mb-1">¡Bienvenida{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!</h2>
          <p className="text-sm text-white/90">Tu cuenta está lista — solo falta un paso</p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          <p className="text-gray-300 text-sm leading-relaxed">
            Para continuar y desbloquear tu plan personalizado (formulario inicial, rutina y dieta),
            por favor completa el pago de la membresía asignada:
          </p>

          {/* Precio */}
          <div
            className="rounded-xl p-5 text-center"
            style={{
              background: 'rgba(var(--brand-primary-glow), 0.08)',
              border: '1.5px solid rgba(var(--brand-primary-glow), 0.4)'
            }}
          >
            <p className="text-xs uppercase tracking-wider text-pink-300 font-semibold mb-1">
              Monto a pagar
            </p>
            <p
              className="text-3xl font-extrabold"
              style={{ color: 'var(--brand-primary)', fontFamily: 'Poppins, sans-serif' }}
              data-testid="pending-amount-display"
            >
              {formatted}
            </p>
            {!useUSD && usd && (
              <p className="text-xs text-gray-400 mt-1">
                Equivalente aprox. ${Number(usd).toFixed(2)} USD
              </p>
            )}
          </div>

          {/* Beneficios */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-200">
              <FaCheck className="text-green-400 flex-shrink-0" />
              <span>Acceso al formulario inicial de evaluación</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-200">
              <FaCheck className="text-green-400 flex-shrink-0" />
              <span>Rutina y plan nutricional 100% personalizados</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-200">
              <FaCheck className="text-green-400 flex-shrink-0" />
              <span>Seguimiento directo con DeisyCFit</span>
            </div>
          </div>

          {/* Botón pagar */}
          <button
            onClick={handlePay}
            disabled={loading || !displayAmount}
            className="w-full py-4 rounded-full font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] flex items-center justify-center gap-2 shadow-lg"
            style={{
              background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))',
              fontFamily: 'Poppins, sans-serif'
            }}
            data-testid="pay-pending-btn"
          >
            <FaCreditCard /> {loading ? 'Redirigiendo a MercadoPago...' : `Pagar ${formatted}`}
          </button>

          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <FaShieldAlt /> Pago seguro vía MercadoPago
          </div>

          {/* Logout opcional */}
          <button
            onClick={() => {
              localStorage.removeItem('deisycfit_token');
              window.location.href = '/login';
            }}
            className="w-full text-xs text-gray-500 hover:text-gray-300 underline-offset-4 hover:underline mt-2"
            data-testid="logout-from-pending"
          >
            Cerrar sesión
          </button>
        </div>
      </motion.div>
    </div>
  );
}
